import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Chips } from "primereact/chips";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Skeleton } from "primereact/skeleton";
import styled from "styled-components";

import {
  DraftComponentType,
  IValidationError,
} from "../../../store/queries/contractModelDrafts";
import {
  SCHEDULE_EXTRACTING,
  SCHEDULE_NOT_READ,
  UNKNOWN_PAYER,
  UNKNOWN_PROVIDER,
  payerIdTitle,
  providerIdTitle,
} from "../copy";
import {
  fieldLabel,
  fieldSpecsFor,
  IFieldSpec,
  RAW_JSON_FIELDS,
} from "./fieldSpecs";
import { PreviewStatusTag } from "./GenerationPreview";
import { PreviewStatus } from "./partialPreview";

/**
 * Resolves a payer/provider id to its display name. `fallbackName` is the name the agent wrote next to the
 * id (`payerName`/`providerName`), used only when nothing better names it (`payerNames.ts`).
 */
export type EntityResolver = (
  key: string,
  value: unknown,
  fallbackName?: string | null
) => string | null;

export interface IPayerOption {
  id: number;
  name: string;
  type: string | null;
}

/** Read `a.b.c` out of a nested object. */
export const getPath = (obj: unknown, path: string): unknown =>
  path
    .split(".")
    .reduce<unknown>(
      (acc, key) =>
        acc && typeof acc === "object"
          ? (acc as Record<string, unknown>)[key]
          : undefined,
      obj
    );

/** Immutably write `a.b.c`. Writing `undefined` removes the key, so an emptied optional field is absent, not null. */
export const setPath = (
  obj: Record<string, unknown>,
  path: string,
  value: unknown
): Record<string, unknown> => {
  const [head, ...rest] = path.split(".");
  const next = { ...obj };
  if (rest.length === 0) {
    if (value === undefined || value === "") delete next[head];
    else next[head] = value;
    return next;
  }
  const child =
    next[head] && typeof next[head] === "object"
      ? (next[head] as Record<string, unknown>)
      : {};
  const updated = setPath(child, rest.join("."), value);
  if (Object.keys(updated).length === 0) delete next[head];
  else next[head] = updated;
  return next;
};

/**
 * CLE-2811 — the smallest a field's cell may get. A date input needs about 170px to show "01/08/2026"
 * and its picker icon in this theme; 11rem (176px) clears it, and a short dropdown label or a currency
 * amount fits too.
 */
const FIELD_MIN_WIDTH = "11rem";

/**
 * A select whose longest option label runs past this many characters is cut off in an 11rem cell
 * ("The claim's total charges", "ASC (ambulatory surgery center)"), so it gets the whole row.
 */
const LONG_OPTION_LABEL_CHARS = 14;

/**
 * Whether a field takes the whole row rather than one cell of the field grid.
 *
 * Free text needs the whole row: in a narrow cell a paragraph wraps into an unreadable column three
 * words across. So do the add/remove tables, a CPT code list (a dozen chips in an 11rem cell is a
 * dozen lines), and a dropdown whose chosen label would otherwise be cut off. Everything else — dates,
 * numbers, money, short dropdowns, short text, checkboxes — shares a row.
 */
export const isWideField = (spec: IFieldSpec): boolean =>
  spec.fullWidth === true ||
  spec.kind === "readonly" ||
  spec.kind === "textarea" ||
  spec.kind === "codes" ||
  spec.kind === "moneyRows" ||
  spec.kind === "percentRows" ||
  (spec.kind === "select" &&
    (spec.options ?? []).some(
      (option) => option.label.length > LONG_OPTION_LABEL_CHARS
    ));

/**
 * The field grid sizes itself to the width the FORM has, not the viewport.
 *
 * PrimeFlex's `md:`/`lg:`/`xl:` column classes key on the viewport, and on this page a wider screen
 * can leave the form LESS room: from 1200px up the conversation docks beside the wizard and takes a
 * third of the screen. At the 1366px layout the reviewer uses the form is about 390px wide, where
 * `xl:col-3` would have put four fields in a row at ~85px each — the clipped "01/08/2…" date again.
 * Tracks of at least FIELD_MIN_WIDTH give two short fields a row there (~190px each), three or four
 * on a wider screen, and one on a phone (`min(100%, …)` keeps one track from overflowing a narrow card).
 */
const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(
    auto-fill,
    minmax(min(100%, ${FIELD_MIN_WIDTH}), 1fr)
  );
  column-gap: 1rem;
  align-items: start;
`;

const FieldCell = styled.div<{ $wide: boolean }>`
  min-width: 0;
  grid-column: ${({ $wide }) => ($wide ? "1 / -1" : "auto")};
`;

const errorFor = (
  errors: IValidationError[] | null,
  key: string
): string | null => errors?.find((e) => e.path === key)?.message ?? null;

interface IFieldProps {
  spec: IFieldSpec;
  value: unknown;
  /** For an `entity` field: the name the agent wrote beside the id, if any. */
  entityName?: string | null;
  error: string | null;
  disabled: boolean;
  resolve?: EntityResolver;
  payers?: IPayerOption[];
  /** Display names of the base rate files loaded for this account — options for a baseRateFileSelect. */
  baseRateFiles?: string[];
  onChange: (value: unknown) => void;
}

function Field({
  spec,
  value,
  entityName = null,
  error,
  disabled,
  resolve,
  payers,
  baseRateFiles,
  onChange,
}: IFieldProps) {
  const id = `pm-${spec.key}`;
  const invalid = error ? "p-invalid" : "";

  const control = () => {
    switch (spec.kind) {
      case "entity": {
        // The reviewer only needs the NAME. The raw id ("Aetna (4)", or a provider UUID) is internal
        // plumbing — it read as clutter and meant nothing to them. CLE-2811 (2026-09-28) — Ani: "Instead of
        // id show the name": never the id, even unresolved ("Unknown payer"); it stays in the tooltip for a
        // super admin (the tool is super-admin only).
        const isProvider = spec.key.toLowerCase().endsWith("providerid");
        const name =
          resolve?.(spec.key, value, entityName) ??
          (isProvider ? UNKNOWN_PROVIDER : UNKNOWN_PAYER);
        return (
          <div className="p-2 surface-100 border-round text-sm">
            {value == null || value === "" ? (
              <span className="text-color-secondary">Not set</span>
            ) : (
              <span
                className="font-semibold"
                title={
                  isProvider ? providerIdTitle(value) : payerIdTitle(value)
                }
              >
                {name}
              </span>
            )}
          </div>
        );
      }
      case "payerSelect": {
        const isMa = spec.payerFilter === "medicare_advantage";
        const opts = (payers ?? [])
          .filter((p) => (isMa ? p.type === "UhcMedicareAdvantage" : true))
          // The name alone (CLE-2811): the list is filterable, and an id means nothing to her.
          .map((p) => ({ label: p.name, value: p.id }));
        return (
          <Dropdown
            inputId={id}
            className={`w-full ${invalid}`}
            value={typeof value === "number" ? value : null}
            options={opts}
            disabled={disabled}
            filter
            showClear={!spec.required}
            placeholder="Select a payer…"
            emptyMessage="No matching payers"
            onChange={(e) => onChange(e.value ?? undefined)}
          />
        );
      }
      case "baseRateFileSelect":
      case "baseRateFileReuseSelect": {
        // Options are the fee schedules actually loaded for this account. The agent's current value is
        // always kept as an option (labelled "current" if it isn't in the list) so a proposal that
        // names a file we can't see is never silently blanked. Filterable, because these lists are long.
        const current = typeof value === "string" ? value : "";
        const names = baseRateFiles ?? [];
        const opts = names.map((n) => ({ label: n, value: n }));
        if (current && !names.includes(current)) {
          opts.unshift({ label: `${current} (current)`, value: current });
        }
        return (
          <Dropdown
            inputId={id}
            className={`w-full ${invalid}`}
            value={current || null}
            options={opts}
            disabled={disabled}
            filter
            showClear={!spec.required}
            placeholder="Select a base rate file…"
            emptyMessage="No base rate files loaded for this account"
            onChange={(e) => onChange(e.value ?? undefined)}
          />
        );
      }
      case "moneyRows": {
        // A carveout's `{ code, amountInCents }` rows, as an add/remove table — never raw JSON, since
        // the reviewer is a contract expert, not an engineer.
        const rows: { code?: string; amountInCents?: number }[] = Array.isArray(
          value
        )
          ? (value as { code?: string; amountInCents?: number }[])
          : [];
        const write = (next: typeof rows) => onChange(next);
        return (
          <div>
            {rows.length === 0 ? (
              <div className="text-color-secondary text-sm mb-2">
                No codes yet.
              </div>
            ) : (
              <div className="flex flex-column gap-2 mb-2">
                {rows.map((r, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <div key={i} className="flex align-items-center gap-2">
                    <InputText
                      className="flex-1"
                      placeholder="Code (e.g. 30140)"
                      value={r.code ?? ""}
                      disabled={disabled}
                      onChange={(e) =>
                        write(
                          rows.map((row, j) =>
                            j === i ? { ...row, code: e.target.value } : row
                          )
                        )
                      }
                    />
                    <InputNumber
                      className="flex-1"
                      placeholder="Amount"
                      mode="currency"
                      currency="USD"
                      locale="en-US"
                      value={
                        typeof r.amountInCents === "number"
                          ? r.amountInCents / 100
                          : null
                      }
                      disabled={disabled}
                      onValueChange={(e) =>
                        write(
                          rows.map((row, j) =>
                            j === i
                              ? {
                                  ...row,
                                  amountInCents:
                                    typeof e.value === "number"
                                      ? Math.round(e.value * 100)
                                      : undefined,
                                }
                              : row
                          )
                        )
                      }
                    />
                    <Button
                      type="button"
                      icon="pi pi-trash"
                      className="p-button-text p-button-sm p-button-danger"
                      aria-label="Remove code"
                      disabled={disabled}
                      onClick={() => write(rows.filter((_, j) => j !== i))}
                    />
                  </div>
                ))}
              </div>
            )}
            <Button
              type="button"
              icon="pi pi-plus"
              label="Add code"
              className="p-button-text p-button-sm p-0"
              disabled={disabled}
              onClick={() => write([...rows, { code: "", amountInCents: 0 }])}
            />
          </div>
        );
      }
      case "percentRows": {
        // A `{ serviceLine, percent }` rules sheet — one whole-percent multiplier per service line.
        const rows: { serviceLine?: string; percent?: number }[] =
          Array.isArray(value)
            ? (value as { serviceLine?: string; percent?: number }[])
            : [];
        const write = (next: typeof rows) => onChange(next);
        return (
          <div>
            {rows.length === 0 ? (
              <div className="text-color-secondary text-sm mb-2">
                No service lines yet.
              </div>
            ) : (
              <div className="flex flex-column gap-2 mb-2">
                {rows.map((r, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <div key={i} className="flex align-items-center gap-2">
                    <InputText
                      className="flex-1"
                      placeholder="Service line (e.g. Radiology)"
                      value={r.serviceLine ?? ""}
                      disabled={disabled}
                      onChange={(e) =>
                        write(
                          rows.map((row, j) =>
                            j === i
                              ? { ...row, serviceLine: e.target.value }
                              : row
                          )
                        )
                      }
                    />
                    <InputNumber
                      className="flex-1"
                      placeholder="Percent"
                      suffix="%"
                      value={typeof r.percent === "number" ? r.percent : null}
                      disabled={disabled}
                      onValueChange={(e) =>
                        write(
                          rows.map((row, j) =>
                            j === i
                              ? {
                                  ...row,
                                  percent:
                                    typeof e.value === "number"
                                      ? e.value
                                      : undefined,
                                }
                              : row
                          )
                        )
                      }
                    />
                    <Button
                      type="button"
                      icon="pi pi-trash"
                      className="p-button-text p-button-sm p-button-danger"
                      aria-label="Remove service line"
                      disabled={disabled}
                      onClick={() => write(rows.filter((_, j) => j !== i))}
                    />
                  </div>
                ))}
              </div>
            )}
            <Button
              type="button"
              icon="pi pi-plus"
              label="Add service line"
              className="p-button-text p-button-sm p-0"
              disabled={disabled}
              onClick={() =>
                write([...rows, { serviceLine: "", percent: 100 }])
              }
            />
          </div>
        );
      }
      case "readonly":
        // The agent talking to the human. Nothing reads it when the construct is built, so an editable
        // box was misleading — it implied the words mattered to the output.
        return value == null || value === "" ? (
          <div className="text-color-secondary text-sm">&mdash;</div>
        ) : (
          <div className="p-2 surface-100 border-round text-sm line-height-3 white-space-pre-wrap">
            {String(value)}
          </div>
        );
      case "boolean":
        return (
          <Checkbox
            inputId={id}
            checked={value === true}
            disabled={disabled}
            onChange={(e) => onChange(e.checked)}
          />
        );
      case "select":
        return (
          <Dropdown
            inputId={id}
            className={`w-full ${invalid}`}
            value={value ?? null}
            options={spec.options}
            disabled={disabled}
            showClear={!spec.required}
            placeholder="Select…"
            onChange={(e) => onChange(e.value ?? undefined)}
          />
        );
      case "codes":
        return (
          <Chips
            inputId={id}
            className={`w-full ${invalid}`}
            value={Array.isArray(value) ? (value as string[]) : []}
            disabled={disabled}
            separator=","
            onChange={(e) => onChange(e.value ?? [])}
          />
        );
      case "money":
        return (
          <InputNumber
            inputId={id}
            className={`w-full ${invalid}`}
            // Stored in cents, edited in dollars — the reviewer should never think in cents.
            value={typeof value === "number" ? value / 100 : null}
            disabled={disabled}
            mode="currency"
            currency="USD"
            locale="en-US"
            onValueChange={(e) =>
              onChange(
                typeof e.value === "number"
                  ? Math.round(e.value * 100)
                  : undefined
              )
            }
          />
        );
      case "percent":
      case "fraction":
      case "number":
        return (
          <InputNumber
            inputId={id}
            className={`w-full ${invalid}`}
            value={typeof value === "number" ? value : null}
            disabled={disabled}
            // A fraction needs decimals (0.85); a whole percent does not.
            maxFractionDigits={spec.kind === "fraction" ? 4 : 2}
            suffix={spec.kind === "percent" ? "%" : undefined}
            onValueChange={(e) =>
              onChange(typeof e.value === "number" ? e.value : undefined)
            }
          />
        );
      case "date":
        return (
          <InputText
            id={id}
            type="date"
            className={`w-full ${invalid}`}
            value={typeof value === "string" ? value.slice(0, 10) : ""}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value || undefined)}
          />
        );
      case "textarea":
        return (
          <InputTextarea
            id={id}
            className={`w-full ${invalid}`}
            rows={3}
            autoResize
            value={typeof value === "string" ? value : ""}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value || undefined)}
          />
        );
      default:
        return (
          <InputText
            id={id}
            className={`w-full ${invalid}`}
            value={value == null ? "" : String(value)}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value || undefined)}
          />
        );
    }
  };

  return (
    <div className="field mb-3">
      <label htmlFor={id} className="block text-sm font-medium mb-1">
        {spec.label}
        {spec.required ? <span className="text-red-500"> *</span> : null}
      </label>
      {control()}
      {error ? (
        <small className="block p-error mt-1">{error}</small>
      ) : (
        spec.help && (
          <small className="block text-color-secondary mt-1">{spec.help}</small>
        )
      )}
    </div>
  );
}

/**
 * CLE-2811 (facts first) — a reimbursement slot whose schedule the pass is still reading: its label, a
 * skeleton line and "Extracting the schedule…", with the plan's status chip. Not an input — there is
 * nothing to edit yet, and the pass will fill it; once it completes the slot is the normal field.
 */
function ExtractingField({
  spec,
  planStatus,
}: {
  spec: IFieldSpec;
  planStatus: PreviewStatus | null;
}) {
  const failed = planStatus === "failed";
  return (
    <div className="field mb-3" data-testid="schedule-extracting">
      <div className="block text-sm font-medium mb-1">{spec.label}</div>
      <div className="p-2 surface-100 border-round text-sm">
        <div className="flex align-items-center flex-wrap gap-2">
          {failed ? null : (
            <i
              className="pi pi-spin pi-spinner"
              style={{ fontSize: "0.8rem", color: "var(--primary-color)" }}
              aria-hidden
            />
          )}
          <span className="text-color-secondary">
            {failed ? SCHEDULE_NOT_READ : SCHEDULE_EXTRACTING}
          </span>
          {planStatus ? <PreviewStatusTag status={planStatus} /> : null}
        </div>
        {failed ? null : (
          <Skeleton width="75%" height="0.8rem" className="mt-2" />
        )}
      </div>
    </div>
  );
}

interface IProps {
  componentType: DraftComponentType;
  /** The current values. Edits are lifted to the parent so the wizard owns dirty state. */
  value: Record<string, unknown>;
  validationErrors: IValidationError[] | null;
  disabled?: boolean;
  /** Resolves payerId/providerId to a display name. */
  resolve?: EntityResolver;
  /** The payers that actually exist — so a routing decision picks one instead of free-typing a name. */
  payers?: IPayerOption[];
  /** Base rate file display names loaded for this account — options for the "file to take a % of" field. */
  baseRateFiles?: string[];
  /** Loaded files as {id, displayName} — lets a reuse_existing pick write BOTH the name and the id. */
  baseRateFileOptions?: { id: string; displayName: string }[];
  /**
   * CLE-2811 (facts first) — the fields still being read (`extractingSlotKeys`) and their plan's status:
   * each renders as "Extracting the schedule…" instead of its input.
   */
  extracting?: { keys: string[]; planStatus: PreviewStatus | null } | null;
  onChange: (next: Record<string, unknown>) => void;
}

/**
 * Renders `proposedModeling` as editable inputs rather than a read-only key/value dump.
 *
 * This is the change Ani asked for: when the AI misreads the effective date, you fix the date — you
 * don't file a note about it. The eval signal is not lost, though: the API snapshots the agent's
 * original on the first edit, so "the agent said X, the human corrected it to Y" is captured for free
 * on every correction, with no justification form to fill in.
 */
function ProposedModelingForm({
  componentType,
  value,
  validationErrors,
  disabled = false,
  resolve,
  payers,
  baseRateFiles,
  baseRateFileOptions,
  extracting = null,
  onChange,
}: IProps) {
  const allSpecs = fieldSpecsFor(componentType);

  // Only the fields that apply to the branch she has chosen. Rendering every branch's fields at once
  // is how you end up asking for the id of a payer that does not exist yet.
  // CLE-2811 — each spec with the label these values call for (`fieldLabel`): an ASC term's one
  // reimbursement slot is "Reimbursement schedule", a hospital's two are "Inpatient/Outpatient …".
  const specs = allSpecs
    .filter((spec) => {
      if (!spec.showWhen) return true;
      const actual = getPath(value, spec.showWhen.key);
      return spec.showWhen.equals.includes(String(actual));
    })
    .map((spec) => ({ ...spec, label: fieldLabel(spec, value) }));
  const rawKeys = RAW_JSON_FIELDS[componentType] ?? [];

  // We used to surface keys the agent emitted that had no field on this construct, as a raw-JSON block.
  // In practice the only things that landed there were benign facets (payerName/providerName), so it
  // was pure noise to a non-technical reviewer. The values are still stored and still round-trip on
  // save — we just no longer show a JSON blob for them.

  return (
    <div>
      <FieldGrid>
        {specs.map((spec) => (
          <FieldCell $wide={isWideField(spec)} key={spec.key}>
            {extracting?.keys.includes(spec.key) ? (
              <ExtractingField spec={spec} planStatus={extracting.planStatus} />
            ) : (
              <Field
                spec={spec}
                value={getPath(value, spec.key)}
                entityName={
                  spec.kind === "entity"
                    ? (getPath(value, spec.key.replace(/Id$/, "Name")) as
                        | string
                        | null
                        | undefined) ?? null
                    : null
                }
                error={errorFor(validationErrors, spec.key)}
                disabled={disabled}
                resolve={resolve}
                payers={payers}
                baseRateFiles={baseRateFiles}
                onChange={(next) => {
                  if (spec.kind === "payerSelect") {
                    // Write the NAME alongside the id. The schema wants a name, and a record that says
                    // "payer 20" and nothing else is unreadable six months later.
                    const picked = payers?.find((p) => p.id === next);
                    let updated = setPath(value, spec.key, next);
                    updated = setPath(updated, "payerName", picked?.name);
                    onChange(updated);
                    return;
                  }
                  if (spec.kind === "baseRateFileReuseSelect") {
                    // Picking a file writes its NAME (what Compare matches on) and its ID (what the
                    // reuse_existing schema requires) together, so the two can never drift apart.
                    const picked = baseRateFileOptions?.find(
                      (f) => f.displayName === next
                    );
                    let updated = setPath(value, spec.key, next);
                    updated = setPath(updated, "baseRateFileId", picked?.id);
                    onChange(updated);
                    return;
                  }
                  onChange(setPath(value, spec.key, next));
                }}
              />
            )}
          </FieldCell>
        ))}
      </FieldGrid>

      {rawKeys.map((key) => (
        <div className="field mb-3" key={key}>
          <label
            htmlFor={`raw-${key}`}
            className="block text-sm font-medium mb-1"
          >
            {key} (JSON)
          </label>
          <InputTextarea
            id={`raw-${key}`}
            className="w-full font-mono text-sm"
            rows={6}
            autoResize
            disabled={disabled}
            value={JSON.stringify(getPath(value, key) ?? null, null, 2)}
            onChange={(e) => {
              try {
                onChange(setPath(value, key, JSON.parse(e.target.value)));
              } catch {
                // Mid-typing JSON is invalid more often than not. Keep the keystroke, let the server
                // be the judge on save rather than fighting the reviewer on every character.
              }
            }}
          />
          <small className="block text-color-secondary mt-1">
            Edited as raw JSON — this shape has too many variants to render as a
            form.
          </small>
        </div>
      ))}
    </div>
  );
}

export default ProposedModelingForm;
